import { guidanceScript, GUIDANCE_CHANNEL } from '../utils/lessonGuidanceBridge.js';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, ArrowUp, BookOpen, CheckCircle2, X } from 'lucide-react';
import { stopActiveSpeech } from '../utils/ttsRuntime';
import { createLessonTtsHost, LESSON_TTS_CHANNEL } from '../utils/lessonTtsBridge';
import '../styles/lessonMonitor.css';

const policy = "default-src 'none'; img-src data: blob:; font-src data:; media-src data: blob:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; connect-src 'none'; form-action 'none'; base-uri 'none'; frame-src 'none'";

const readableArea = value => String(value || '')
  .replaceAll('_', ' ')
  .replace(/\b\w/g, letter => letter.toUpperCase());

export default function InstructionLessonDialog({ lesson, onClose, onComplete, onPrevious, onNext, position, total, navigating = false, navigationError = '' }) {
  const [teacherLocked,setTeacherLocked]=useState(false);
  const frame = useRef(null);
  const dialog = useRef(null);
  const closeButton = useRef(null);
  const session = useRef(null);
  const sessionLesson = useRef(lesson?.id);
  if (!session.current || sessionLesson.current !== lesson?.id) {
    sessionLesson.current = lesson?.id;
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    session.current = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  useEffect(() => {
    const lessonSession = session.current;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    stopActiveSpeech('instruction-opened');
    const detach = createLessonTtsHost({ frame: frame.current, session: lessonSession });
    closeButton.current?.focus({ preventScroll: true });

    const onKey = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialog.current) return;
      const focusable = [...dialog.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter(node => node.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      detach();
      stopActiveSpeech('instruction-closed');
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey, true);
      if (previouslyFocused instanceof window.HTMLElement && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [lesson?.id]);

  useEffect(()=>{
    const lessonSession = session.current;
    window.dispatchEvent(new window.CustomEvent('lesson-monitor-state',{detail:{...lesson,session:lessonSession}}));
    const receive=event=>{const data=event.data;if(event.source!==frame.current?.contentWindow||event.origin!=='null'||data?.channel!==GUIDANCE_CHANNEL||data.session!==lessonSession)return;
      if(data.type==='annotation-report')window.dispatchEvent(new window.CustomEvent('lesson-monitor-annotation-report',{detail:{session:lessonSession,report:data.report}}));
      if(data.type==='state')window.dispatchEvent(new window.CustomEvent('lesson-monitor-telemetry',{detail:{session:lessonSession,telemetry:data.telemetry}}));
      if(data.type==='reading-state')window.dispatchEvent(new window.CustomEvent('lesson-monitor-reading',{detail:{session:lessonSession,reading:data.reading}}));
      if(data.type==='ack')window.dispatchEvent(new window.CustomEvent('lesson-monitor-ack',{detail:data}));
      if(data.type==='stop-speech')stopActiveSpeech('teacher-stopped-lesson');
    };
    const command=event=>{if(event.detail.session===lessonSession)frame.current?.contentWindow?.postMessage({channel:GUIDANCE_CHANNEL,session:lessonSession,type:'command',...event.detail},'*');};
    const lock=event=>{if(event.detail.session===lessonSession){setTeacherLocked(event.detail.locked);frame.current?.contentWindow?.postMessage({channel:GUIDANCE_CHANNEL,session:lessonSession,type:'lock',locked:event.detail.locked},'*');}};
    const annotations=event=>{if(event.detail.session===lessonSession)frame.current?.contentWindow?.postMessage({channel:GUIDANCE_CHANNEL,session:lessonSession,type:'annotation-board',...event.detail},'*');};
    const fastScroll=event=>{if(event.detail.session===lessonSession)frame.current?.contentWindow?.postMessage({channel:GUIDANCE_CHANNEL,session:lessonSession,type:'guide-scroll',seq:event.detail.seq,viewport:event.detail.viewport},'*');};
    window.addEventListener('lesson-monitor-annotations',annotations);
    window.addEventListener('lesson-monitor-fast-scroll',fastScroll);
    window.addEventListener('message',receive);window.addEventListener('lesson-monitor-command',command);window.addEventListener('lesson-monitor-lock',lock);
    return()=>{window.removeEventListener('lesson-monitor-annotations',annotations);window.removeEventListener('lesson-monitor-fast-scroll',fastScroll);window.removeEventListener('message',receive);window.removeEventListener('lesson-monitor-command',command);window.removeEventListener('lesson-monitor-lock',lock);window.dispatchEvent(new window.CustomEvent('lesson-monitor-state',{detail:{closed:true,session:lessonSession}}));};
  },[lesson.id]);

  const scrollLessonToTop = () => frame.current?.contentWindow?.postMessage({
    channel: LESSON_TTS_CHANNEL,
    session: session.current,
    action: 'scroll-top'
  }, '*');

  const titleId = `instruction-lesson-title-${session.current}`;
  const details = [readableArea(lesson.area), lesson.currentItemNumber ? `Question ${lesson.currentItemNumber}` : '']
    .filter(Boolean).join(' · ');
  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${policy}"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body><script>window.__AssessNowLessonTTS=Object.freeze({channel:'${LESSON_TTS_CHANNEL}',version:1,session:'${session.current}'});window.addEventListener('message',function(event){var data=event.data;if(data&&data.channel==='${LESSON_TTS_CHANNEL}'&&data.session==='${session.current}'&&data.action==='scroll-top'){window.scrollTo({top:0,left:0,behavior:'smooth'});}});</script>${lesson.html || ''}<script>${guidanceScript(session.current)}</script></body></html>`;

  return createPortal(<div role="presentation" data-instruction-lesson-backdrop="true" className="instruction-lesson-backdrop"
    onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
    style={{ position: 'fixed', inset: 0, zIndex: 2147483600,
      padding: 'max(8px,env(safe-area-inset-top)) max(8px,env(safe-area-inset-right)) max(8px,env(safe-area-inset-bottom)) max(8px,env(safe-area-inset-left))',
      background: 'rgba(5,13,27,.84)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
    <section ref={dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} className="instruction-lesson-dialog"
      data-instruction-lesson-dialog="full" data-lesson-id={lesson.id}
      style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0,
        width: 'min(1440px,calc(100vw - 16px))', height: 'min(96vh,1080px)', maxHeight: 'calc(100vh - 16px)',
        background: '#fff', color: '#172033', border: '1px solid rgba(174,232,220,.72)',
        borderRadius: 'clamp(12px,1.5vw,22px)', overflow: 'hidden', boxShadow: '0 32px 100px rgba(0,0,0,.52)' }}>
      <header className="instruction-lesson-header" style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, padding: 'clamp(12px,1.8vw,20px) clamp(14px,2.2vw,26px)',
        background: 'linear-gradient(135deg,#102a45,#16415a)', color: 'white', borderBottom: '1px solid #34647a' }}>
        <div style={{ minWidth: 0, flex: '1 1 520px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, textTransform: 'uppercase',
            letterSpacing: '.12em', color: '#aee8dc', fontWeight: 850 }}><BookOpen size={17} aria-hidden="true" /> {lesson.phase === 'before' ? 'Study my lessons' : 'Lesson Help'}
            {position && total && <span style={{ marginLeft: 6, padding: '3px 8px', borderRadius: 999, background: 'rgba(174,232,220,.17)', color: '#e5f7f2' }}>Lesson {position} of {total}</span>}</div>
          <h2 id={titleId} style={{ margin: '5px 0 0', fontSize: 'clamp(1.15rem,2.2vw,1.65rem)', lineHeight: 1.2,
            overflowWrap: 'anywhere' }}>{lesson.title}</h2>
          {details && <div className="instruction-lesson-details" style={{ marginTop: 5, color: '#c9eee6', fontSize: '.82rem', fontWeight: 750 }}>{details}</div>}
          {lesson.objective && <div className="instruction-lesson-objective" style={{ marginTop: 4, color: '#e4f4f1', fontSize: '.88rem', lineHeight: 1.35,
            overflowWrap: 'anywhere' }}>{lesson.objective}</div>}
        </div>
        <button ref={closeButton} type="button" onClick={onClose} aria-label="Close lesson and return to the assessment"
          style={{ minWidth: 102, minHeight: 46, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            border: '1px solid #d7eee8', background: '#fff', color: '#102a45', borderRadius: 11,
            padding: '10px 15px', fontWeight: 850, fontSize: '.95rem', cursor: 'pointer', boxShadow: '0 3px 10px rgba(0,0,0,.2)' }}>
          <X size={20} aria-hidden="true" /> Close
        </button>
      </header>

      <div className="instruction-lesson-content" style={{ flex: '1 1 auto', minHeight: 0, position: 'relative', background: '#eef3f6' }}>
        <iframe ref={frame} title={lesson.title} sandbox="allow-scripts" referrerPolicy="no-referrer" srcDoc={srcDoc}
          style={{ display: 'block', width: '100%', height: '100%', border: 0, background: 'white' }} />
      </div>

      <footer className="instruction-lesson-footer" style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap',
        padding: '10px clamp(14px,2.2vw,26px)', borderTop: '1px solid #cbd5e1', background: '#f8fafc' }}>
        <button type="button" onClick={scrollLessonToTop} disabled={teacherLocked}
          style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 13px',
            border: '1px solid #94a3b8', borderRadius: 10, background: '#fff', color: '#334155', fontWeight: 800, cursor: 'pointer' }}>
          <ArrowUp size={18} aria-hidden="true" /> Back to lesson top
        </button>
        <span style={{ flex: '1 1 auto', minWidth: 8 }} />
        {navigationError && <span role="alert" style={{ flex: '1 1 100%', color: '#991b1b', fontSize: 13 }}>{navigationError}</span>}
        {onPrevious && <button type="button" onClick={onPrevious} disabled={navigating||teacherLocked} aria-label="Previous lesson"
          style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 13px',
            border: '1px solid #64748b', borderRadius: 10, background: '#fff', color: '#1e293b', fontWeight: 850, cursor: navigating ? 'wait' : 'pointer' }}>
          <ArrowLeft size={17} aria-hidden="true" /> Previous lesson
        </button>}
        {onNext && <button type="button" onClick={onNext} disabled={navigating||teacherLocked} aria-label="Next lesson"
          style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 13px',
            border: '1px solid #0b594a', borderRadius: 10, background: '#e6f6f1', color: '#0b594a', fontWeight: 850, cursor: navigating ? 'wait' : 'pointer' }}>
          Next lesson <ArrowRight size={17} aria-hidden="true" />
        </button>}
        <button type="button" onClick={onClose}
          style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
            border: '1px solid #64748b', borderRadius: 10, background: '#fff', color: '#1e293b', fontWeight: 850, cursor: 'pointer' }}>
          <X size={18} aria-hidden="true" /> {lesson.phase === 'before' ? 'Return to lesson list' : onComplete ? 'Close and return to question' : 'Close preview'}
        </button>
        {onComplete && <button type="button" onClick={onComplete} disabled={navigating||teacherLocked}
          style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px',
            border: '1px solid #0b594a', borderRadius: 10, background: '#116d59', color: 'white', fontWeight: 850,
            cursor: navigating ? 'wait' : 'pointer', boxShadow: '0 4px 12px rgba(17,109,89,.22)' }}>
          <CheckCircle2 size={19} aria-hidden="true" /> {onNext ? 'Finished · Next lesson' : 'I finished this lesson'}
        </button>}
      </footer>
    </section>
  </div>, document.body);
}
